import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';

export interface Integration {
  id: string;
  name: string;
  type: 'aws' | 'gcp' | 'azure' | 'okta' | 'github' | 'hetzner';
  status: 'active' | 'inactive' | 'error';
  config: any;
  last_sync?: string;
  organization_id?: string;
  created_by?: string;
  created_at: string;
  updated_at: string;
}

export interface Scan {
  id: string;
  integration_id?: string;
  status: 'queued' | 'running' | 'completed' | 'failed' | 'cancelled';
  findings_count: number;
  ruleset: string;
  started_at?: string;
  completed_at?: string;
  organization_id?: string;
  created_by?: string;
  created_at: string;
}

export interface Finding {
  id: string;
  scan_id?: string;
  rule_id: string;
  title: string;
  description?: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  status: 'open' | 'waived' | 'fixed';
  resource_ref: string;
  evidence_id?: string;
  waived_reason?: string;
  organization_id?: string;
  created_at: string;
  updated_at: string;
  scan?: {
    id: string;
    created_at: string;
    integration?: {
      name: string;
      type: string;
    };
  };
}

export const useScanners = () => {
  const [integrations, setIntegrations] = useState<Integration[]>([]);
  const [scans, setScans] = useState<Scan[]>([]);
  const [findings, setFindings] = useState<Finding[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();

  const fetchIntegrations = async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const { data, error } = await supabase.functions.invoke('scanners-api', {
        body: { action: 'list_integrations' }
      });

      if (error) {
        console.error('Error fetching integrations:', error);
        setError(error.message);
        return;
      }

      setIntegrations(data?.integrations || []);
    } catch (err) {
      console.error('Error fetching integrations:', err);
      setError('Failed to fetch integrations');
    } finally {
      setLoading(false);
    }
  };

  const connectIntegration = async (type: Integration['type'], config: any, name: string) => {
    try {
      const { data, error } = await supabase.functions.invoke('scanners-api', {
        body: {
          action: 'connect',
          type,
          config,
          name
        }
      });

      if (error) throw error;

      await fetchIntegrations();
      return data;
    } catch (error) {
      console.error('Error connecting integration:', error);
      throw error;
    }
  };

  const deleteIntegration = async (integrationId: string) => {
    try {
      const { data, error } = await supabase.functions.invoke('scanners-api', {
        body: { action: 'delete_integration', integration_id: integrationId }
      });
      if (error) throw error;
      await fetchIntegrations();
      await fetchScans();
      await fetchFindings();
      return data;
    } catch (error) {
      console.error('Error deleting integration:', error);
      throw error;
    }
  };
  const runScan = async (integrationId: string, ruleset: string = 'default') => {
    try {
      const { data, error } = await supabase.functions.invoke('scanners-api', {
        body: {
          action: 'run_scan',
          integration_id: integrationId,
          ruleset
        }
      });

      if (error) throw error;

      await fetchScans();
      return data;
    } catch (error) {
      console.error('Error running scan:', error);
      throw error;
    }
  };

  const fetchScans = async (integrationId?: string) => {
    if (!user) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const { data, error } = await supabase.functions.invoke('scanners-api', {
        body: { 
          action: 'list_scans',
          integration_id: integrationId
        }
      });

      if (error) {
        console.error('Error fetching scans:', error);
        setError(error.message);
        return;
      }

      setScans(data?.scans || []);
    } catch (err) {
      console.error('Error fetching scans:', err);
      setError('Failed to fetch scans');
    } finally {
      setLoading(false);
    }
  };

  const fetchFindings = async (scanId?: string, status?: Finding['status'], severity?: Finding['severity']) => {
    if (!user) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const { data, error } = await supabase.functions.invoke('scanners-api', {
        body: { 
          action: 'list_findings',
          scan_id: scanId,
          status,
          severity
        }
      });

      if (error) {
        console.error('Error fetching findings:', error);
        setError(error.message);
        return;
      }

      setFindings(data?.findings || []);
    } catch (err) {
      console.error('Error fetching findings:', err);
      setError('Failed to fetch findings');
    } finally {
      setLoading(false);
    }
  };

  const updateFinding = async (findingId: string, updates: {
    status?: Finding['status'];
    waived_reason?: string;
    evidence_id?: string;
  }) => {
    try {
      const { error } = await supabase.functions.invoke('scanners-api', {
        body: {
          action: 'update_finding',
          finding_id: findingId,
          updates
        }
      });

      if (error) throw error;

      await fetchFindings();
    } catch (error) {
      console.error('Error updating finding:', error);
      throw error;
    }
  };

  const deleteFinding = async (findingId: string) => {
    try {
      const { error } = await supabase.functions.invoke('scanners-api', {
        body: {
          action: 'delete_finding',
          finding_id: findingId
        }
      });

      if (error) throw error;

      await fetchFindings();
    } catch (error) {
      console.error('Error deleting finding:', error);
      throw error;
    }
  };

  const addFindingToTasks = async (finding: Finding) => {
    try {
      const { error } = await supabase.functions.invoke('tasks-api', {
        body: {
          action: 'create',
          type: 'remediation',
          title: `[Security] ${finding.title}`,
          description: finding.description || '',
          source: {
            finding_id: finding.id,
            severity: finding.severity,
            resource_ref: finding.resource_ref
          }
        }
      });

      if (error) throw error;
    } catch (error) {
      console.error('Error adding finding to tasks:', error);
      throw error;
    }
  };

  useEffect(() => {
    fetchIntegrations();
    fetchScans();
    fetchFindings();
  }, [user]);

  return {
    integrations,
    scans,
    findings,
    loading,
    error,
    connectIntegration,
    deleteIntegration,
    runScan,
    updateFinding,
    deleteFinding,
    addFindingToTasks,
    fetchScans,
    fetchFindings,
    refetch: () => {
      fetchIntegrations();
      fetchScans();
      fetchFindings();
    }
  };
};